import React, { useEffect, useState } from 'react';
import { Card, Badge } from '@/components/ui';
import { adminStatsService, SystemHealthStatus } from '@/services/adminStats.service';

interface SystemHealthProps {
  className?: string;
}

interface MetricItemProps {
  label: string;
  value: string | number;
  unit?: string;
  status?: 'good' | 'warning' | 'critical';
  icon: React.ReactNode;
  progressValue?: number;
  progressMax?: number;
  isLoading?: boolean;
}

const MetricItem: React.FC<MetricItemProps> = ({
  label,
  value,
  unit,
  status,
  icon,
  progressValue,
  progressMax,
  isLoading,
}) => {
  const getStatusColor = () => {
    switch (status) {
      case 'good':
        return 'text-success-500';
      case 'warning':
        return 'text-warning-500';
      case 'critical':
        return 'text-danger-500';
      default:
        return 'text-secondary-500';
    }
  };

  const getProgressColor = () => {
    if (!progressValue || !progressMax) return 'bg-secondary-300';
    const percentage = (progressValue / progressMax) * 100;
    if (percentage < 60) return 'bg-success-500';
    if (percentage < 80) return 'bg-warning-500';
    return 'bg-danger-500';
  };

  if (isLoading) {
    return (
      <div className="p-4 bg-secondary-50 rounded-xl animate-pulse">
        <div className="flex items-center justify-between mb-3">
          <div className="h-4 w-20 bg-secondary-200 rounded" />
          <div className="h-8 w-8 bg-secondary-200 rounded-lg" />
        </div>
        <div className="h-6 w-16 bg-secondary-200 rounded" />
      </div>
    );
  }

  return (
    <div className="p-4 bg-secondary-50 rounded-xl hover:bg-secondary-100 transition-colors">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-secondary-500 uppercase tracking-wide">
          {label}
        </span>
        <div className={`${getStatusColor()}`}>{icon}</div>
      </div>
      <div className="flex items-baseline space-x-1">
        <span className="text-2xl font-bold text-secondary-900">{value}</span>
        {unit && <span className="text-sm text-secondary-500">{unit}</span>}
      </div>
      {progressValue !== undefined && progressMax !== undefined && (
        <div className="mt-3">
          <div className="w-full h-2 bg-secondary-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${getProgressColor()}`}
              style={{ width: `${Math.min((progressValue / progressMax) * 100, 100)}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

interface StatusIndicatorProps {
  label: string;
  status: 'healthy' | 'degraded' | 'down';
  isLoading?: boolean;
}

const StatusIndicator: React.FC<StatusIndicatorProps> = ({ label, status, isLoading }) => {
  const statusConfig = {
    healthy: {
      color: 'bg-success-500',
      text: 'Healthy',
      badge: 'success' as const,
    },
    degraded: {
      color: 'bg-warning-500',
      text: 'Degraded',
      badge: 'warning' as const,
    },
    down: {
      color: 'bg-danger-500',
      text: 'Down',
      badge: 'danger' as const,
    },
  };

  const config = statusConfig[status];

  if (isLoading) {
    return (
      <div className="flex items-center justify-between py-3 animate-pulse">
        <div className="h-4 w-24 bg-secondary-200 rounded" />
        <div className="h-6 w-16 bg-secondary-200 rounded-full" />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between py-3">
      <div className="flex items-center space-x-3">
        <span className={`w-2 h-2 rounded-full ${config.color} animate-pulse`} />
        <span className="text-sm text-secondary-700">{label}</span>
      </div>
      <Badge variant={config.badge} size="sm">
        {config.text}
      </Badge>
    </div>
  );
};

export const SystemHealth: React.FC<SystemHealthProps> = ({ className = '' }) => {
  const [health, setHealth] = useState<SystemHealthStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const fetchHealth = async () => {
    try {
      setIsLoading(true);
      const data = await adminStatsService.getSystemHealth();
      setHealth(data);
      setLastRefresh(new Date());
    } catch (err) {
      setError('Failed to load system health');
      console.error('Error fetching system health:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchHealth();

    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  const getResponseTimeStatus = (ms: number): 'good' | 'warning' | 'critical' => {
    if (ms < 200) return 'good';
    if (ms < 500) return 'warning';
    return 'critical';
  };

  const getErrorRateStatus = (rate: number): 'good' | 'warning' | 'critical' => {
    if (rate < 1) return 'good';
    if (rate < 5) return 'warning';
    return 'critical';
  };

  const getOverallStatus = (): 'healthy' | 'degraded' | 'down' => {
    if (!health) return 'down';
    if (health.databaseStatus === 'down') return 'down';
    if (health.databaseStatus === 'degraded' || health.errorRate > 5) return 'degraded';
    return 'healthy';
  };

  if (error) {
    return (
      <Card className={className}>
        <Card.Header>
          <h2 className="text-lg font-semibold text-secondary-900">System Health</h2>
        </Card.Header>
        <Card.Body>
          <div className="h-48 flex items-center justify-center">
            <div className="text-center">
              <svg
                className="mx-auto h-12 w-12 text-danger-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <p className="mt-2 text-sm text-secondary-600">{error}</p>
              <button
                onClick={fetchHealth}
                className="mt-2 text-sm text-primary-600 hover:text-primary-700"
              >
                Try again
              </button>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  }

  const overallStatus = getOverallStatus();
  const overallStatusConfig = {
    healthy: { text: 'All Systems Operational', color: 'text-success-600', bg: 'bg-success-50' },
    degraded: { text: 'Partial System Issues', color: 'text-warning-600', bg: 'bg-warning-50' },
    down: { text: 'System Outage', color: 'text-danger-600', bg: 'bg-danger-50' },
  };

  return (
    <Card className={className}>
      <Card.Header>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-secondary-900">System Health</h2>
            <p className="text-xs text-secondary-400 mt-1">
              Last updated: {lastRefresh.toLocaleTimeString()}
            </p>
          </div>
          <button
            onClick={fetchHealth}
            disabled={isLoading}
            className="p-2 text-secondary-500 hover:text-secondary-700 hover:bg-secondary-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <svg
              className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
        </div>
      </Card.Header>
      <Card.Body>
        {/* Overall Status Banner */}
        <div className={`p-3 rounded-xl ${overallStatusConfig[overallStatus].bg} mb-4`}>
          <div className="flex items-center space-x-2">
            {overallStatus === 'healthy' ? (
              <svg className="w-5 h-5 text-success-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : overallStatus === 'degraded' ? (
              <svg className="w-5 h-5 text-warning-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-danger-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
            <span className={`text-sm font-medium ${overallStatusConfig[overallStatus].color}`}>
              {overallStatusConfig[overallStatus].text}
            </span>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <MetricItem
            label="API Response"
            value={health?.apiResponseTime ?? 0}
            unit="ms"
            status={health ? getResponseTimeStatus(health.apiResponseTime) : undefined}
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            }
            isLoading={isLoading}
          />

          <MetricItem
            label="Active Sessions"
            value={health?.activeSessions ?? 0}
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
            }
            isLoading={isLoading}
          />

          <MetricItem
            label="Error Rate"
            value={health?.errorRate.toFixed(2) ?? '0.00'}
            unit="%"
            status={health ? getErrorRateStatus(health.errorRate) : undefined}
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            }
            isLoading={isLoading}
          />

          <MetricItem
            label="Uptime"
            value={health?.uptime.toFixed(2) ?? '0.00'}
            unit="%"
            status={health && health.uptime > 99.9 ? 'good' : health && health.uptime > 99 ? 'warning' : 'critical'}
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            }
            isLoading={isLoading}
          />
        </div>

        {/* Resource Usage */}
        <div className="space-y-3 mb-4">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-secondary-500">CPU Usage</span>
              <span className="text-xs font-bold text-secondary-700">
                {health?.cpuUsage.toFixed(1) ?? 0}%
              </span>
            </div>
            <div className="w-full h-2 bg-secondary-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  (health?.cpuUsage ?? 0) < 60
                    ? 'bg-success-500'
                    : (health?.cpuUsage ?? 0) < 80
                    ? 'bg-warning-500'
                    : 'bg-danger-500'
                }`}
                style={{ width: `${health?.cpuUsage ?? 0}%` }}
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-secondary-500">Memory Usage</span>
              <span className="text-xs font-bold text-secondary-700">
                {health?.memoryUsage.toFixed(1) ?? 0}%
              </span>
            </div>
            <div className="w-full h-2 bg-secondary-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  (health?.memoryUsage ?? 0) < 60
                    ? 'bg-success-500'
                    : (health?.memoryUsage ?? 0) < 80
                    ? 'bg-warning-500'
                    : 'bg-danger-500'
                }`}
                style={{ width: `${health?.memoryUsage ?? 0}%` }}
              />
            </div>
          </div>
        </div>

        {/* Service Status */}
        <div className="divide-y divide-secondary-100">
          <StatusIndicator
            label="Database"
            status={health?.databaseStatus ?? 'down'}
            isLoading={isLoading}
          />
        </div>
      </Card.Body>
    </Card>
  );
};

export default SystemHealth;
